import React, { Component, Fragment, Suspense } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import gql from 'graphql-tag';
import { graphql } from 'react-apollo';
import { connect } from 'react-redux';
import { setLocation } from '../Home/actions';
import Nav from '../../components/Nav';
import Footer from '../../components/Footer';
import loadCrisp from '../../utils/crisp';
import loadGoogleAnalytics from '../../utils/analytics';
import MobileNavigation from '../../components/MobileNavigation';
import LocationSearch from '../../components/LocationSearch';
import Loader from '../../components/Loader';

const importHome           = () => import('../Home');
const importAboutUs        = () => import('../AboutUs');
const importContactUs      = () => import('../ContactUs');
const importApplyPartner   = () => import('../ApplyPartner');
const importAuthCallback   = () => import('../AuthCallback');
const importCreateAccount  = () => import('../CreateAccount');
const importVerification   = () => import('../Verification');
const importFailedResponse = () => import('../FailedResponse');
const importResetPassword  = () => import('../ResetPassword');
const importEditAccount    = () => import('../EditAccount');
const importShops          = () => import('../Shops');
const importMenu           = () => import('../Menu');
const importMenuCategories = () => import('../MenuCategories');
const importSingleMenuItem = () => import('../SingleMenuItem');
const importLogin          = () => import('../Login');
const importOrders         = () => import('../Orders');
const importOrderTracking  = () => import('../OrderTracking');
const importOrderResponse  = () => import('../OrderResponse');
const importOutOfBounds    = () => import('../OutOfBounds');
const importCheckout       = () => import('../Checkout');

const lazyRoutes = [
  importHome, importAboutUs, importContactUs, importApplyPartner, importAuthCallback,
  importCreateAccount, importVerification, importFailedResponse, importResetPassword,
  importEditAccount, importShops, importMenu, importMenuCategories, importSingleMenuItem,
  importLogin, importOrders, importOrderTracking, importOrderResponse,
  importOutOfBounds, importCheckout,
];

const AsyncHome           = React.lazy(importHome);
const AsyncAboutUs        = React.lazy(importAboutUs);
const AsyncContactUs      = React.lazy(importContactUs);
const AsyncApplyPartner   = React.lazy(importApplyPartner);
const AsyncAuthCallback   = React.lazy(importAuthCallback);
const AsyncCreateAccount  = React.lazy(importCreateAccount);
const AsyncVerification   = React.lazy(importVerification);
const AsyncFailedResponse = React.lazy(importFailedResponse);
const AsyncResetPassword  = React.lazy(importResetPassword);
const AsyncEditAccount    = React.lazy(importEditAccount);
const AsyncShops          = React.lazy(importShops);
const AsyncMenu           = React.lazy(importMenu);
const AsyncMenuCategories = React.lazy(importMenuCategories);
const AsyncSingleMenuItem = React.lazy(importSingleMenuItem);
const AsyncLogin          = React.lazy(importLogin);
const AsyncOrders         = React.lazy(importOrders);
const AsyncOrderTracking  = React.lazy(importOrderTracking);
const AsyncOrderResponse  = React.lazy(importOrderResponse);
const AysncOutOfBounds    = React.lazy(importOutOfBounds);
const AsyncCheckout       = React.lazy(importCheckout);

class ChunkErrorBoundary extends Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidMount() {
    sessionStorage.removeItem('chunkReloaded');
  }
  componentDidCatch(error) {
    const message = (error && (error.message || error.name)) || '';
    const isChunkError = /Loading chunk|ChunkLoadError/i.test(message);
    if (isChunkError && !sessionStorage.getItem('chunkReloaded')) {
      sessionStorage.setItem('chunkReloaded', '1');
      window.location.reload();
    }
  }
  render() {
    return this.state.hasError ? <Loader /> : this.props.children;
  }
}

const shouldPreloadRoutes = () => {
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!conn) return true;
  if (conn.saveData) return false;
  return !/(^|-)2g$/.test(conn.effectiveType || '');
};

class App extends Component {
  state = {
    location: false,
  }

  componentDidMount() {
    const isDev = process.env.REACT_APP_NODE_ENV === 'development' ||
      process.env.REACT_APP_NODE_ENV === 'staging';
    // loadCrisp(isDev);
    loadGoogleAnalytics(isDev);

    if (shouldPreloadRoutes()) {
      const preload = () => lazyRoutes.forEach(load => load().catch(() => {}));
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(preload, { timeout: 5000 });
      } else {
        setTimeout(preload, 2000);
      }
    }
  }

  render() {
    const { data: { order }, setAddress, maps } = this.props;

    if (this.state.location) {
      return (
        <Fragment>
          <Nav order={order && order.order} />
          <LocationSearch
            setDeliveryAddress={setAddress}
            maps={maps}
            close={() => this.setState({ location: false })}
          />
        </Fragment>
      );
    }

    return (
      <Fragment>
        <Nav order={order && order.order} open={() => this.setState({ location: true })} />
        <ChunkErrorBoundary>
          <Suspense fallback={<Loader />}>
            <Switch>
              <Route exact path='/' component={AsyncHome} />
              <Route exact path='/about-us' component={AsyncAboutUs} />
              <Route exact path='/contact-us' component={AsyncContactUs} />
              <Route exact path='/become-a-partner' component={AsyncApplyPartner} />
              <Route exact path='/auth' component={AsyncAuthCallback} />
              <Route exact path='/login' component={AsyncLogin} />
              <Route exact path='/register' component={AsyncCreateAccount} />
              <Route exact path='/reset-password' component={AsyncResetPassword} />
              <Route exact path='/register' component={AsyncCreateAccount} />
              <Route exact path='/verification' component={AsyncVerification} />
              <Route exact path='/edit-account' component={AsyncEditAccount} />
              <Route exact path='/order-failed' component={AsyncFailedResponse} />
              <Route exact path='/:shop/menu/:category' component={AsyncMenu} />
              <Route exact path='/:slug/categories' component={AsyncMenuCategories} />
              <Route exact path='/:shop/menu/:category/:slug' component={AsyncSingleMenuItem} />
              <Route exact path='/checkout' component={AsyncCheckout} />
              <Route exact path='/order-summary' render={() => <Redirect to='/checkout' />} />
              <Route exact path='/order-response/:uuid/:reference' component={AsyncOrderResponse} />
              <Route exact path='/order-status/:id' component={AsyncOrderTracking} />
              <Route exact path='/orders' component={AsyncOrders} />
              <Route exact path='/shops' component={AsyncShops} />
              <Route exact path='/location' component={AysncOutOfBounds} />
              <Route exact path='/pay-online' render={() => <Redirect to='/checkout' />} />
              <Redirect from='/become-a-partner' to='/become-a-partner' />
              <Redirect from='/edit-account' to='/edit-account' />
              <Redirect from='/past-orders' to='/orders' />
            </Switch>
          </Suspense>
        </ChunkErrorBoundary>
        <div className='spacer' />
        {window.innerWidth < 1024 && <MobileNavigation />}
        <Footer />
      </Fragment>
    )
  }
}

const mapStateToProps = state => ({
  user: state.auth.user,
  maps: state.location,
});

const mapDispatchToProps = dispatch => ({
  setAddress: location => dispatch(setLocation(location))
});

const NOT_AUTHENTICATED = 'NOT_AUTHENTICATED';

const LIVE_ORDER_QUERY = gql`
  query($userId: String!) {
		order(userId: $userId) {
      order {
        uuid
        status
      }
    }
	}
`;

App = graphql(LIVE_ORDER_QUERY, {
  options: props => ({
    variables: {
      userId: (props.user && props.user.id) || NOT_AUTHENTICATED
    },
    fetchPolicy: 'network-only'
  }),
  name: 'data'
})(App);

export default connect(mapStateToProps, mapDispatchToProps)(App);